/**
 * Bubble letters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const bubbleLettersPack: ToilePack = {
    slug: 'bubble-letters',
    category: 'illustration',
    name: 'Bubble letters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'alphabet', label: 'Full alphabet', subject: 'a chart of the 26 capitals "A" to "Z" as fat rounded bubble outlines in five even rows, each letter closed and empty inside for coloring'},
        {key: 'lowercase', label: 'Lowercase', subject: 'a chart of the lowercase bubble letters "a" to "z" in five rows, soft round counters, the dots of i and j drawn as small circles'},
        {key: 'word', label: 'Single word', subject: 'the word "HAPPY" as large overlapping bubble outlines filling the page width, each letter tucked slightly behind the next, white inside'},
    ],
    styles: [
        {key: 'plain', label: 'Plain outline', style: 'one even medium outline, perfectly round puffy curves, nothing inside the letters', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'inline', label: 'Double line', style: 'a double outline, a second thin line running inside the first like an inline, rounded corners', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'shine', label: 'Shine and dots', style: 'a bold puffy outline with a small curved highlight stroke and a few tiny dots inside each letter', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
    ],
    directions: ['a name', 'a number', 'stars'],
    directionHint: 'try: a name',
    seo: {
        title: 'Bubble Letters: Alphabet, Lowercase, One Word',
        metaDescription: 'Nine printable bubble letter sheets: the capitals, the lowercase and one word, each in plain, double-line and shine-and-dot outlines.',
        h1: 'Bubble letters: full alphabet, lowercase, a single word',
        tagline: 'Nine sheets in three outline styles, every letter closed and empty inside for coloring.',
        intro: 'Bubble letters are the round, puffy capitals a child traces for a poster, a classroom door or a birthday banner. These nine sheets are black outline on white: a full A to Z chart, a lowercase chart and the word HAPPY, each in a plain outline, a double line or a shine-and-dot finish. Ask for a name, a number or stars and the same bubble outline is drawn for that subject.',
        useCases: [
            'a classroom door or bulletin board title',
            'a birthday banner',
            'a poster for a school display',
            'a coloring page for a child',
        ],
        faq: [
            {q: 'Are these printable?', a: 'Each sheet is black line on a plain white background, with no color or shading, so it can be printed in black ink.'},
            {q: 'Can I get a name or a number instead of HAPPY?', a: 'Yes. Ask for a name, a number or stars, and the same bubble outline is drawn for that subject. The samples are the nine shown; a different subject is a request.'},
            {q: 'What is the difference between the three styles?', a: 'Plain outline is one even line with nothing inside. Double line adds a thin second line inside each letter. Shine and dots adds a small curved highlight and a few tiny dots.'},
            {q: 'Are the letters meant to be colored in?', a: 'Yes. Each letter is closed and left empty inside, so a child can fill it with color.'},
        ],
    },
}
